/**
 * API client hook that wraps fetch with auth token management.
 * Handles token refresh on 401 and logout on auth failure.
 */

import { useCallback } from 'react';
import { useAuthStore } from '../stores';
import {
  type RequestOptions,
  type ApiResponse,
  defaultApiConfig,
  buildUrl,
  buildHeaders,
  createApiError,
  isAuthError,
} from '@callbridge/ui';

export function useApiClient() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const tokenRefreshed = useAuthStore((s) => s.tokenRefreshed);
  const logout = useAuthStore((s) => s.logout);

  const request = useCallback(
    async <T = unknown>(options: RequestOptions): Promise<T> => {
      const config = {
        ...defaultApiConfig,
        getAccessToken: () => accessToken,
      };

      const url = buildUrl(config.baseUrl, options.path, options.params);
      const headers = buildHeaders(config, options);

      const res = await fetch(url, {
        method: options.method,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      if (res.ok) {
        return (await res.json()) as T;
      }

      // Try token refresh on 401
      if (isAuthError(res.status) && !options.skipAuth && refreshToken) {
        const refreshRes = await fetch(
          buildUrl(config.baseUrl, '/auth/refresh'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          },
        );

        if (refreshRes.ok) {
          const data = (await refreshRes.json()) as {
            data: { tokens: { accessToken: string; refreshToken: string } };
          };
          tokenRefreshed(data.data.tokens.accessToken, data.data.tokens.refreshToken);

          // Retry original request with new token
          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${data.data.tokens.accessToken}`,
          };
          const retryRes = await fetch(url, {
            method: options.method,
            headers: retryHeaders,
            body: options.body ? JSON.stringify(options.body) : undefined,
          });

          if (retryRes.ok) {
            return (await retryRes.json()) as T;
          }
        }

        // Refresh failed — force logout
        logout();
      }

      const errorBody = (await res.json().catch(() => null)) as ApiResponse | null;
      throw createApiError(res.status, errorBody ?? undefined);
    },
    [accessToken, refreshToken, tokenRefreshed, logout],
  );

  return { request };
}
